import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: 'Success!',
        description: 'Thank you for contacting us. We\'ll be in touch shortly.',
        duration: 5000,
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        budget: '',
        message: '',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen">
      <Helmet>
        <title>Get Free Construction Quote Toronto & GTA | Contact Oracle Project Group | (647) 631-8914</title>
        <meta name="description" content="Request free construction consultation in Toronto & GTA. Licensed commercial contractor for interior fit-outs, renovations, design-build. Call (647) 631-8914 or email for quote." />
        <link rel="canonical" href="https://oracleprojectgroup.com/contact" />
        <meta property="og:title" content="Contact Oracle Project Group | Free Construction Quote Toronto" />
        <meta property="og:description" content="Get free consultation for your commercial construction project in Toronto & GTA. Call (647) 631-8914." />
        <meta property="og:url" content="https://oracleprojectgroup.com/contact" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Oracle Project Group | Free Construction Quote Toronto" />
        <meta name="twitter:description" content="Get free consultation for your commercial construction project in Toronto & GTA. Call (647) 631-8914." />
        <meta name="keywords" content="construction quote Toronto, free consultation, commercial contractor contact, construction estimate GTA, general contractor quote" />
      </Helmet>
      {/* Hero Section */}
      <header className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
             src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&q=80"
          alt="Professional residential construction site - wooden framing and modern home building"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            imageRendering: 'high-quality',
            imageRendering: '-webkit-optimize-contrast',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            transform: 'translateZ(0)',
            filter: 'contrast(1.06) saturate(1.08) brightness(0.98)',
          }}
          loading="eager"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Let's discuss your next construction project. Get in touch with our team today.
          </p>
        </div>
      </header>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-[#0F1115] mb-6">Get In Touch</h2>
              <div className="w-24 h-1 bg-[#C6A45B] mb-6"></div>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Have a project in mind? We'd love to hear from you. Fill out the form or reach out to us directly.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F1115] mb-1">Phone</h3>
                    <a 
                      href="tel:+16476318914" 
                      className="text-gray-600 hover:text-[#D4AF37] transition-colors hover:underline cursor-pointer"
                      style={{ textDecoration: 'none', pointerEvents: 'auto' }}
                      target="_self"
                      rel="noopener"
                    >
                      (647) 631-8914
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F1115] mb-1">Email</h3>
                    <a
                      href="mailto:sarangan@oracleprojectgroup.com"
                      className="text-gray-600 hover:text-[#D4AF37] transition-colors hover:underline cursor-pointer"
                      style={{ textDecoration: 'none', pointerEvents: 'auto' }}
                      target="_self"
                      rel="noopener"
                    >
                      sarangan@oracleprojectgroup.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-16 ml-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_build-marble/artifacts/fr2rv7oy_Color%20logo%20-%20no%20background.png"
                  alt="Oracle Project Group logo - Professional construction company in Toronto and GTA"
                  className="w-80 h-auto"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#F6F6F7] p-8 rounded-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#0F1115] mb-2">
                    Name *
                  </label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#0F1115] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#0F1115] mb-2">
                    Phone *
                  </label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="+1 (xxx) xxx-xxxx"
                  />
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-[#0F1115] mb-2">
                    Project Type *
                  </label>
                  <Select onValueChange={(value) => handleSelectChange('projectType', value)} value={formData.projectType}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="interior-fitout">Interior Fit-Out</SelectItem>
                      <SelectItem value="design-build">Design-Build</SelectItem>
                      <SelectItem value="general-contracting">General Contracting</SelectItem>
                      <SelectItem value="renovation">Renovation</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-[#0F1115] mb-2">
                    Budget Range (Optional)
                  </label>
                  <Select onValueChange={(value) => handleSelectChange('budget', value)} value={formData.budget}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-50k">Under $50,000</SelectItem>
                      <SelectItem value="50k-100k">$50,000 - $100,000</SelectItem>
                      <SelectItem value="100k-250k">$100,000 - $250,000</SelectItem>
                      <SelectItem value="250k-500k">$250,000 - $500,000</SelectItem>
                      <SelectItem value="over-500k">Over $500,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#0F1115] mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
